import { useState, useRef, useEffect, useMemo } from 'react';
import { useInfiniteProjects } from '../../../hooks/API/settings.api';
import { useDebounce } from '../../../hooks/useDebounce';

const ProjectSelectDropdown = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const debouncedSearch = useDebounce(searchTerm, 300);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteProjects({
    search: debouncedSearch,
    pageSize: 20,
    enabled: open,
  });

  const projects = useMemo(
    () => data?.pages?.flatMap((page) => page.projects) || [],
    [data]
  );

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 48;

    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleSelect = (project) => {
    onChange(String(project.project_id));
    setSelectedLabel(project.project_name);
    setOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev) {
        setSearchTerm('');
      }
      return !prev;
    });
  };

  const displayLabel = value && selectedLabel ? selectedLabel : placeholder;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          disabled
            ? 'bg-gray-100 cursor-not-allowed text-gray-400'
            : 'hover:bg-gray-50'
        }`}
      >
        <span
          className={`truncate ${
            value && selectedLabel ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {displayLabel}
        </span>
        <i
          className={`fas fa-chevron-down text-gray-500 text-xs transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search projects..."
                autoFocus
                className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-2.5 text-gray-400 text-xs">
                <i className="fas fa-search" />
              </span>
            </div>
          </div>

          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-60 overflow-y-auto"
          >
            {isLoading ? (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              </div>
            ) : isError ? (
              <div className="px-3 py-4 text-sm text-red-500 text-center">
                Failed to load projects
              </div>
            ) : projects.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No projects found
              </div>
            ) : (
              <>
                {projects.map((project) => {
                  const isSelected =
                    String(project.project_id) === String(value);

                  return (
                    <button
                      key={project.project_id}
                      type="button"
                      onClick={() => handleSelect(project)}
                      className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {project.project_name}
                    </button>
                  );
                })}

                {isFetchingNextPage && (
                  <div className="flex justify-center items-center py-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  </div>
                )}

                {!hasNextPage && projects.length > 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
                    All projects loaded
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelectDropdown;
